import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { firstListItem } from '@/constants/defaultValues';
import { apiPath } from '@/constants/defaultValues';
import { Delete, Get, Put, Post } from '@/api/api-middleware';
import { getCompanyID, getEmployeeID } from '@/helpers/Utils';
import { getLabel } from '@/helpers/Utils';

const initialState = {
  scheduleCatgoryList: [],
  scheduleCatgory: {},
  notification: { show: false, code: '' },
  loading: false,
  error: '',
};

export const fetchScheduleCategory = createAsyncThunk(
  'scheduleCategory/fetchScheduleCategory',
  async (params, thunkAPI) => {
    try {
      const response = await Get({
        url: `${apiPath}api/Lookup/ScheduleCategory/GetAll/${getCompanyID()}`,
      });
      const mappedData =
        response?.data?.data?.map((item) => ({
          id: item.id,
          nameEn: item.nameEn,
          nameAr: item.nameAr,
          code: item.code,
          label: item.name,
          value: item.id,
        })) ?? [];
      return [firstListItem, ...mappedData];
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const addScheduleCategory = createAsyncThunk(
  'scheduleCategory/addScheduleCategory',
  async (params, thunkAPI) => {
    try {
      // Determine if it's a POST or PUT request based on the presence of params.id
      const isUpdate = params?.id && params.id !== '';
      const url = isUpdate
        ? `${apiPath}api/Lookup/ScheduleCategory/Update/${params.id}` // PUT route with ID
        : `${apiPath}api/Lookup/ScheduleCategory/Add`; // POST route

      const method = isUpdate ? Put : Post; // Use Put if updating, Post if creating

      const response = await method({
        url: url,
        data: params,
      });
      // Dispatch fetchScheduleCategory to refresh the list
      thunkAPI.dispatch(fetchScheduleCategory());
      if (response?.data?.errCode === '200') {
        return response?.data?.data;
      } else return response?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteScheduleCategory = createAsyncThunk(
  'scheduleCategory/deleteScheduleCategory',
  async (params, thunkAPI) => {
    try {
      const response = await Delete({
        url: `${apiPath}api/Lookup/ScheduleCategory/Delete/${params.id}`,
      });

      // Dispatch fetchScheduleCategory to refresh the list
      if (response?.data?.errCode === '200')
        thunkAPI.dispatch(fetchScheduleCategory());

      return response?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const scheduleCategorySlice = createSlice({
  name: 'scheduleCategory',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchScheduleCategory.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchScheduleCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.scheduleCatgoryList = action.payload;
        state.notification = { code: '', show: false };
        state.error = '';
      })
      .addCase(fetchScheduleCategory.rejected, (state, action) => {
        state.loading = false;
        state.scheduleCatgoryList = [];
        state.error = action.payload ?? 'An error occurred';
        state.notification = { code: '', show: false };
      });

    builder
      .addCase(addScheduleCategory.pending, (state) => {
        state.loading = true;
      })
      .addCase(addScheduleCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.scheduleCatgory = action.payload?.errCode ? null : action.payload;
        state.notification = {
          code: action.payload?.errCode ? action.payload?.errCode : '200',
          show: true,
        };
        state.error = '';
      })
      .addCase(addScheduleCategory.rejected, (state, action) => {
        state.loading = false;
        state.scheduleCatgory = {};
        state.notification = { code: '500', show: true };
        state.error = action.payload ?? 'An error occurred';
      });
    builder
      .addCase(deleteScheduleCategory.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteScheduleCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.notification = { code: action.payload?.errCode, show: true };
        state.error = '';
      })
      .addCase(deleteScheduleCategory.rejected, (state, action) => {
        state.loading = false;
        state.notification = { code: '500', show: true };
        state.error = action.payload ?? 'An error occurred';
      });
  },
});

export default scheduleCategorySlice.reducer;
